package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.layout.Box
import androidx.compose.ui.Alignment
import com.evoapps.evoshift.domain.explain.Calculation
import com.evoapps.evoshift.domain.explain.MonthExplanations
import com.evoapps.evoshift.domain.explain.Side

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.MonthlyResult
import com.evoapps.evoshift.domain.model.MonthlyResultSide
import com.evoapps.evoshift.domain.model.SalesIncomeSource
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.monthly_result_actual
import evoshift.composeapp.generated.resources.monthly_result_commission
import evoshift.composeapp.generated.resources.monthly_result_costs
import evoshift.composeapp.generated.resources.monthly_result_coverage
import evoshift.composeapp.generated.resources.monthly_result_fixed_costs
import evoshift.composeapp.generated.resources.monthly_result_fixed_revenue
import evoshift.composeapp.generated.resources.monthly_result_forecast
import evoshift.composeapp.generated.resources.monthly_result_labour
import evoshift.composeapp.generated.resources.monthly_result_margin
import evoshift.composeapp.generated.resources.monthly_result_net_sales
import evoshift.composeapp.generated.resources.monthly_result_net_sales_total
import evoshift.composeapp.generated.resources.monthly_result_no_margin
import evoshift.composeapp.generated.resources.monthly_result_note
import evoshift.composeapp.generated.resources.monthly_result_one_off
import evoshift.composeapp.generated.resources.monthly_result_shared_costs
import evoshift.composeapp.generated.resources.monthly_result_one_off_revenue
import evoshift.composeapp.generated.resources.monthly_result_own_sales
import evoshift.composeapp.generated.resources.monthly_result_recurring_revenue
import evoshift.composeapp.generated.resources.monthly_result_result
import evoshift.composeapp.generated.resources.monthly_result_revenue
import evoshift.composeapp.generated.resources.monthly_result_sales_income_mixed
import evoshift.composeapp.generated.resources.monthly_result_title
import evoshift.composeapp.generated.resources.planned
import org.jetbrains.compose.resources.stringResource

/** Test tags of the result card (layout checks look only inside it). */
object MonthlyResultTags {
    const val CARD = "monthly_result_card"
}

/** Positive results in green, losses in red (the Financials hero uses the same pair). */
internal val ResultGain = Color(0xFF2E7D32)
internal val ResultLoss = Color(0xFFC62828)

/**
 * SH-46, the owner's "Wynik Finansowy" sheet: the month's revenue (the sales income plus the fixed
 * revenue and the ledger's revenue) minus its costs (employer labour cost, fixed costs, one-off
 * expenses) = the result, with the margin; plan and actual side by side, then how much of the
 * month the actual covers and the forecast for the whole month. The sales income is the
 * commission earned on the net sales (shown first, dimmed, as its base), or without a commission
 * the net sales themselves, one line of the revenue. Fixed revenue shows only where the workplace
 * has it.
 */
@Composable
fun MonthlyResultCard(
    result: MonthlyResult,
    currency: String,
    modifier: Modifier = Modifier,
    /** SH-152: each side's rows explained (tap a figure to see how it was calculated); none without. */
    explained: (Side) -> MonthExplanations.ResultSide? = { null }
) {
    val plan = result.plan
    val actual = result.actual
    val planWhy = explained(Side.PLAN)
    val actualWhy = explained(Side.ACTUAL)
    val forecastWhy = explained(Side.FORECAST)
    fun money(value: Double) = formatMoney(value, currency)
    fun hasAny(pick: (MonthlyResultSide) -> Double) = pick(plan) != 0.0 || pick(actual) != 0.0
    // The row's two figures with their calculations.
    @Composable
    fun Line(label: String, pick: (MonthlyResultSide) -> Double, why: (MonthExplanations.ResultSide) -> Calculation, bold: Boolean = false) =
        ResultRow(label, money(pick(plan)), money(pick(actual)), bold = bold, planWhy = planWhy?.let(why), actualWhy = actualWhy?.let(why), currency = currency)

    RegularCard(modifier.fillMaxWidth().testTag(MonthlyResultTags.CARD)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                stringResource(Res.string.monthly_result_title),
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = DeepNavy
            )
            Spacer(Modifier.height(8.dp))
            ResultRow("", stringResource(Res.string.planned), stringResource(Res.string.monthly_result_actual), header = true)

            // The sales income: the commission (its base, the net sales, dimmed above it), or
            // without a commission the net sales themselves, which are the owner's.
            val base = DeepNavy.copy(alpha = 0.6f)
            when (result.salesIncomeSource) {
                SalesIncomeSource.NET_SALES ->
                    Line(stringResource(Res.string.monthly_result_own_sales), { it.salesIncome }, { it.salesIncome })
                SalesIncomeSource.COMMISSION -> {
                    ResultRow(stringResource(Res.string.monthly_result_net_sales), money(plan.netSales), money(actual.netSales), planColor = base, actualColor = base)
                    if (hasAny { it.commission }) {
                        Line(stringResource(Res.string.monthly_result_commission), { it.commission }, { it.salesIncome })
                    }
                }
                SalesIncomeSource.MIXED -> {
                    ResultRow(stringResource(Res.string.monthly_result_net_sales_total), money(plan.netSales), money(actual.netSales), planColor = base, actualColor = base)
                    Line(stringResource(Res.string.monthly_result_sales_income_mixed), { it.salesIncome }, { it.salesIncome })
                }
            }
            if (hasAny { it.fixedRevenue }) {
                Line(stringResource(Res.string.monthly_result_fixed_revenue), { it.fixedRevenue }, { it.fixedRevenue })
            }
            // SH-87: revenue items of the month's ledger, when it has any.
            if (hasAny { it.recurringRevenue }) {
                Line(stringResource(Res.string.monthly_result_recurring_revenue), { it.recurringRevenue }, { it.recurringRevenue })
            }
            if (hasAny { it.oneOffRevenue }) {
                Line(stringResource(Res.string.monthly_result_one_off_revenue), { it.oneOffRevenue }, { it.oneOffRevenue })
            }
            Line(stringResource(Res.string.monthly_result_revenue), { it.revenue }, { it.revenue }, bold = true)
            Divider()

            Line(stringResource(Res.string.monthly_result_labour), { it.labourCost }, { it.labourCost })
            Line(stringResource(Res.string.monthly_result_fixed_costs), { it.fixedCosts }, { it.fixedCosts })
            Line(stringResource(Res.string.monthly_result_one_off), { it.oneOffCosts }, { it.oneOffCosts })
            // SH-131: the workplace's parts of the owner's shared costs.
            if (plan.sharedCosts > 0.0 || actual.sharedCosts > 0.0) {
                Line(stringResource(Res.string.monthly_result_shared_costs), { it.sharedCosts }, { it.sharedCosts })
            }
            Line(stringResource(Res.string.monthly_result_costs), { it.costs }, { it.costs }, bold = true)
            Divider()

            ResultRow(
                stringResource(Res.string.monthly_result_result),
                money(plan.result),
                money(actual.result),
                bold = true,
                planColor = resultColor(plan.result),
                actualColor = resultColor(actual.result),
                planWhy = planWhy?.result,
                actualWhy = actualWhy?.result,
                currency = currency
            )
            val none = stringResource(Res.string.monthly_result_no_margin)
            ResultRow(
                stringResource(Res.string.monthly_result_margin),
                plan.marginPercent?.let { formatPercent(it) } ?: none,
                actual.marginPercent?.let { formatPercent(it) } ?: none,
                planWhy = planWhy?.margin?.takeIf { plan.marginPercent != null },
                actualWhy = actualWhy?.margin?.takeIf { actual.marginPercent != null },
                currency = currency
            )

            Spacer(Modifier.height(8.dp))
            if (result.days > 0) {
                Note(stringResource(Res.string.monthly_result_coverage, result.daysWithTakings, result.days))
            }
            ExplainableAmount(
                stringResource(Res.string.monthly_result_forecast, money(result.forecast.result)),
                forecastWhy?.result,
                currency,
                style = MaterialTheme.typography.bodySmall,
                fontWeight = FontWeight.SemiBold,
                color = resultColor(result.forecast.result)
            )
            Note(stringResource(Res.string.monthly_result_note))
        }
    }
}

private fun resultColor(value: Double): Color = if (value < 0) ResultLoss else ResultGain

@Composable
private fun Divider() {
    HorizontalDivider(Modifier.padding(vertical = 4.dp), color = DeepNavy.copy(alpha = 0.12f))
}

@Composable
private fun Note(text: String) {
    Text(text, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.6f))
}

/** A line of the result: its name, then the plan and the actual right-aligned in equal columns. */
@Composable
private fun ResultRow(
    label: String,
    plan: String,
    actual: String,
    bold: Boolean = false,
    header: Boolean = false,
    planColor: Color = DeepNavy,
    actualColor: Color = DeepNavy,
    planWhy: Calculation? = null,
    actualWhy: Calculation? = null,
    currency: String = ""
) {
    val base: TextStyle = if (header) MaterialTheme.typography.labelSmall else MaterialTheme.typography.bodySmall
    val style = base.copy(fontWeight = if (bold || header) FontWeight.Bold else FontWeight.Normal)
    val dim = if (header) 0.6f else 1f
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(label, Modifier.weight(1.15f), style = style, color = DeepNavy.copy(alpha = if (header) dim else 0.85f))
        Box(Modifier.weight(1f), contentAlignment = Alignment.CenterEnd) {
            // SH-158: a figure that changes (takings entered, a shift moved) rolls to its new value.
            ExplainableAmount(plan, planWhy, currency, style = style, color = planColor.copy(alpha = dim), textAlign = TextAlign.End, animated = !header)
        }
        Box(Modifier.weight(1f), contentAlignment = Alignment.CenterEnd) {
            ExplainableAmount(actual, actualWhy, currency, style = style, color = actualColor.copy(alpha = dim), textAlign = TextAlign.End, animated = !header)
        }
    }
}
